import { useState, useEffect, useCallback, useRef } from 'react';
import { isEnabledForPlatform } from '../utils/accountSettingPlatformList';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSpinner,
  faDownload,
  faRotateLeft
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { useAccount } from '../contexts/AccountContext';
import { useAccountSettings } from '../hooks/useAccountSettings';
import { getUserData } from '../utils/userUtils';
import StatusBadge from './StatusBadge';
import './PacingDetails.css';

const POLLING_INTERVAL = 15000; // 15 seconds

// Excel-derived rows can carry a non-numeric dailyBudget (e.g. a stray header
// row parsed as data) — render the raw value instead of crashing on toFixed.
const formatBudgetCell = (value) => {
  if (value == null || value === '') return '-';
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : String(value);
};

/**
 * PacingDetails component for viewing pacing schedule details and progress
 */
const PacingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAccountIdToUse } = useAccount();
  const [user] = useState(() => getUserData());
  const accountId = getAccountIdToUse();
  const { settings: accountSettings } = useAccountSettings(accountId, user);

  const [pacingData, setPacingData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPolling, setIsPolling] = useState(true);
  const pollingTimeoutRef = useRef(null);
  const [revertingCampaignId, setRevertingCampaignId] = useState(null);
  const [sortByStatus, setSortByStatus] = useState(null); // null = default, 'asc', 'desc'

  const fetchPacingDetails = useCallback(async () => {
    if (!accountId || !id) return;

    try {
      const response = await api.get(getApiUrl(`/api/v2/pacing/${id}`), {
        params: {
          accountId,
          userId: user?.userId || user?.id
        }
      });

      if (response.status === 200) {
        setPacingData(response.data);
        setError(null);

        // Stop polling if processing is complete
        const status = response.data.status;
        if (status === 'completed' || status === 'completed_with_errors' || status === 'failed') {
          setIsPolling(false);
        }
      }
    } catch (err) {
      console.error('Error fetching pacing details:', err);
      setError(err.response?.data?.error || 'Failed to load pacing details');
    } finally {
      setIsLoading(false);
    }
  }, [accountId, id, user]);

  // Initial fetch and polling
  useEffect(() => {
    fetchPacingDetails();
  }, [fetchPacingDetails]);

  // Polling effect
  useEffect(() => {
    if (isPolling && !isLoading) {
      pollingTimeoutRef.current = setTimeout(() => {
        fetchPacingDetails();
      }, POLLING_INTERVAL);
    }

    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, [isPolling, isLoading, fetchPacingDetails, pacingData]);

  const getPlatformName = (platform) => {
    const names = {
      'dv360': 'DV360',
      'ttd': 'The Trade Desk',
      'facebook': 'Facebook',
      'meta': 'Meta',
      'google_search': 'Google Ads',
      'google-ads-search': 'Google Ads Search',
      'google_ads': 'Google Ads Search',
      'google_ads_search': 'Google Ads Search',
      'tiktok': 'TikTok',
      'reddit': 'Reddit',
      'linkedin': 'LinkedIn',
      'madhive': 'MadHive'
    };
    return names[platform?.toLowerCase()] || platform || 'Unknown';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  /**
   * Revert a campaign's budget - prompts user for the budget value to revert to
   */
  const handleRevertBudget = async (campaign) => {
    if (revertingCampaignId) return;

    // Prompt user for the budget value to revert to
    const inputValue = window.prompt(
      `Enter the budget value to revert campaign ${campaign.platformCampaignId} to:\n\nCurrent budget: ${formatCurrency(campaign.dailyBudget)}`,
      ''
    );

    if (inputValue === null) return; // User cancelled

    const revertBudget = parseFloat(inputValue.replace(/[$,]/g, ''));
    if (isNaN(revertBudget) || revertBudget < 0) {
      alert('Please enter a valid budget amount');
      return;
    }

    const confirmMsg = `Are you sure you want to change the budget for campaign ${campaign.platformCampaignId} from ${formatCurrency(campaign.dailyBudget)} to ${formatCurrency(revertBudget)}?`;
    if (!window.confirm(confirmMsg)) return;

    setRevertingCampaignId(campaign.id);

    try {
      const response = await api.post(
        getApiUrl(`/api/v2/pacing/${id}/campaigns/${campaign.id}/revert`),
        { revertBudget },
        {
          params: {
            accountId,
            userId: user?.userId || user?.id
          }
        }
      );

      if (response.data.success) {
        // Refresh the data to show updated status
        fetchPacingDetails();
      }
    } catch (err) {
      console.error('Error reverting budget:', err);
      alert(err.response?.data?.error || 'Failed to revert budget');
    } finally {
      setRevertingCampaignId(null);
    }
  };

  /**
   * Download errors as CSV file
   * Extracts error details from import-campaign or edit-campaign message responses
   */
  const downloadErrorsCsv = () => {
    if (!pacingData?.campaigns) return;

    const failedCampaigns = pacingData.campaigns.filter(c => c.status === 'failed');
    if (failedCampaigns.length === 0) return;

    // Escape CSV values
    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    // Build CSV content
    const headers = ['Row', 'Platform Campaign ID', 'Campaign Name', 'Daily Budget', 'Platform', 'Error Source', 'Error Message', 'Error Details'];
    const rows = failedCampaigns.map(campaign => {
      const errorDetails = campaign.errorDetails;
      const errorSource = errorDetails?.source || (campaign.editMessageId ? 'edit-campaign' : 'import-campaign');
      const platform = errorDetails?.platform || pacingData.platform || '';

      // Get primary error message
      const errorMessage = errorDetails?.error || campaign.errorMessage || 'Unknown error';

      // Extract detailed error information from errors array
      let errorDetailsStr = '';
      if (errorDetails?.errors) {
        const errors = errorDetails.errors;
        if (Array.isArray(errors)) {
          // Join all errors, handling both string and object errors
          errorDetailsStr = errors.map(e => typeof e === 'object' ? JSON.stringify(e) : String(e)).join(' | ');
        } else if (typeof errors === 'object') {
          errorDetailsStr = JSON.stringify(errors);
        } else {
          errorDetailsStr = String(errors);
        }
      }

      return [
        campaign.rowNumber,
        escapeCSV(campaign.platformCampaignId),
        escapeCSV(campaign.campaignName || ''),
        campaign.dailyBudget,
        escapeCSV(platform),
        escapeCSV(errorSource),
        escapeCSV(errorMessage),
        escapeCSV(errorDetailsStr)
      ].join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${pacingData.name || 'pacing'}_errors_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * Check if download errors button is enabled for the current platform
   * Account setting: download-pacing-errors-button: "facebook, google-ads-search"
   */
  const isDownloadErrorsEnabled = () => {
    if (!accountSettings || !pacingData?.platform) return false;
    return isEnabledForPlatform(accountSettings['download-pacing-errors-button'], pacingData.platform);
  };

  /**
   * Download the original uploaded Excel file
   */
  const [isDownloadingFile, setIsDownloadingFile] = useState(false);

  const downloadOriginalFile = async () => {
    if (!pacingData?.hasDownloadableFile || isDownloadingFile) return;

    setIsDownloadingFile(true);
    try {
      const response = await api.get(getApiUrl(`/api/v2/pacing/${id}/download-file`), {
        params: {
          accountId,
          userId: user?.userId || user?.id
        }
      });

      if (response.data?.publicUrl) {
        // Open the signed URL in a new tab to trigger download
        window.open(response.data.publicUrl, '_blank');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      setError('Failed to download file');
    } finally {
      setIsDownloadingFile(false);
    }
  };

  if (isLoading && !pacingData) {
    return (
      <div className="pacing-details-loading">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <p>Loading pacing details...</p>
      </div>
    );
  }

  if (error && !pacingData) {
    return (
      <div className="pacing-details-error">
        <p>{error}</p>
        <button onClick={() => navigate('/pacing')}>Back to Pacing</button>
      </div>
    );
  }

  if (!pacingData) return null;

  const { progress, campaigns: rawCampaigns, filterInfo } = pacingData;

  const STATUS_ORDER = {
    'failed': 0, 'revert_failed': 1, 'retrying_import': 2, 'retrying_edit': 3,
    'processing': 4, 'pending': 5, 'updated': 6, 'reverted': 7, 'completed': 8
  };

  const campaigns = sortByStatus
    ? [...rawCampaigns].sort((a, b) => {
        const orderA = STATUS_ORDER[a.status] ?? 99;
        const orderB = STATUS_ORDER[b.status] ?? 99;
        return sortByStatus === 'asc' ? orderA - orderB : orderB - orderA;
      })
    : rawCampaigns;

  const skippedRows = filterInfo?.skippedRows || [];
  const duplicateRows = filterInfo?.duplicateRows || [];
  const summary = filterInfo?.summary || null;

  // Get reason label for display
  const getReasonLabel = (reason) => {
    const labels = {
      // Validation errors
      'missing_campaign_id': 'Missing Campaign ID',
      'invalid_daily_budget': 'Invalid Daily Budget',
      'missing_advertiser_id': 'Missing Advertiser ID',
      // Filtering reasons
      'zero_daily_budget': 'Zero Daily Budget',
      'budget_oti_exceeded': 'Budget OTI > 80%',
      'daily_budget_oti_on_target': 'Budget OTI On Target',
      'campaign_paused': 'Campaign Paused',
      'flight_status_not_live': 'Flight Not Live',
      'duplicate': 'Duplicate'
    };
    return labels[reason] || reason;
  };

  const hasWarnings = skippedRows.length > 0 || duplicateRows.length > 0;

  return (
    <div className="pacing-details-container">
      {/* Header - matching CampaignDetailPage style */}
      <div className="pacing-detail-header">
        <button
          onClick={() => navigate('/pacing')}
          className="back-button"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Pacing
        </button>

        <div className="pacing-info">
          <h1>{pacingData.name}</h1>
          <div className="pacing-meta">
            <span className="destination">{getPlatformName(pacingData.platform)}</span>
            <span className="folder">{pacingData.folderName || 'No folder'}</span>
            <StatusBadge status={pacingData.status} />
          </div>
        </div>
      </div>

      {/* Progress Section */}
      <div className="pacing-progress-section">
        <div className="progress-header">
          <h2>Progress</h2>
          {isPolling && (
            <span className="polling-indicator">
              <FontAwesomeIcon icon={faSpinner} spin /> Auto-refreshing every 15s
            </span>
          )}
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div
              className="progress-fill completed"
              style={{ width: `${(progress.completed / progress.total) * 100}%` }}
            />
            <div
              className="progress-fill reverted"
              style={{ width: `${((progress.reverted || 0) / progress.total) * 100}%` }}
            />
            <div
              className="progress-fill failed"
              style={{ width: `${(progress.failed / progress.total) * 100}%` }}
            />
          </div>
          <span className="progress-text">{progress.percentComplete}%</span>
        </div>
        <div className="progress-stats">
          <div className="stat">
            <span className="stat-value">{progress.total}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="stat pending">
            <span className="stat-value">{progress.pending}</span>
            <span className="stat-label">Pending</span>
          </div>
          <div className="stat processing">
            <span className="stat-value">{progress.processing}</span>
            <span className="stat-label">Processing</span>
          </div>
          {progress.retrying > 0 && (
            <div className="stat retrying">
              <span className="stat-value">{progress.retrying}</span>
              <span className="stat-label">Retrying</span>
            </div>
          )}
          <div className="stat completed">
            <span className="stat-value">{progress.completed}</span>
            <span className="stat-label">Completed</span>
          </div>
          {(progress.reverted || 0) > 0 && (
            <div className="stat reverted">
              <span className="stat-value">{progress.reverted}</span>
              <span className="stat-label">Reverted</span>
            </div>
          )}
          <div className="stat failed">
            <span className="stat-value">{progress.failed}</span>
            <span className="stat-label">Failed</span>
          </div>
        </div>
      </div>

      {/* Warnings Section - show if there are skipped or duplicate rows */}
      {hasWarnings && (
        <div className="pacing-filter-section">
          <h2>⚠️ Warnings ({skippedRows.length + duplicateRows.length})</h2>
          <p className="filter-summary-text">
            {summary && `${summary.totalRows} total rows in file, ${summary.uniqueCampaigns} campaigns to pace`}
            {summary?.skippedCount > 0 && `, ${summary.skippedCount} filtered`}
            {summary?.duplicateCount > 0 && `, ${summary.duplicateCount} duplicates`}
          </p>

          {/* Filtered Rows Table */}
          {skippedRows.length > 0 && (
            <div className="skipped-rows-table-container">
              <h3>Filtered Rows ({skippedRows.length})</h3>
              <table className="skipped-rows-table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Campaign ID</th>
                    <th>Reason</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {skippedRows.map((row, idx) => (
                    <tr key={idx}>
                      <td className="row-number">{row.rowNumber}</td>
                      <td className="platform-id">{row.campaignId}</td>
                      <td className="filter-reason">
                        <span className={`reason-badge ${row.reason}`}>
                          {getReasonLabel(row.reason)}
                        </span>
                      </td>
                      <td className="filter-message">{row.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Duplicate Rows Table */}
          {duplicateRows.length > 0 && (
            <div className="skipped-rows-table-container duplicate-rows">
              <h3>Duplicate Rows ({duplicateRows.length})</h3>
              <table className="skipped-rows-table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Campaign ID</th>
                    <th>Budget (Ignored)</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {duplicateRows.map((row, idx) => (
                    <tr key={idx}>
                      <td className="row-number">{row.rowNumber}</td>
                      <td className="platform-id">{row.campaignId}</td>
                      <td className="daily-budget">{formatBudgetCell(row.dailyBudget)}</td>
                      <td className="filter-message">{row.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Campaigns Table */}
      <div className="pacing-campaigns-section">
        <div className="campaigns-section-header">
          <h2>Campaigns ({campaigns.length})</h2>
          {progress.failed > 0 && isDownloadErrorsEnabled() && (
            <button
              className="download-errors-button"
              onClick={downloadErrorsCsv}
              title="Download error details as CSV"
            >
              <FontAwesomeIcon icon={faDownload} />
              Download Errors CSV
            </button>
          )}
        </div>
        <div className="campaigns-table-container">
          <table className="campaigns-table">
            <thead>
              <tr>
                <th>Row</th>
                <th>Platform Campaign ID</th>
                <th>Campaign Name</th>
                <th>Daily Budget</th>
                <th
                  className="sortable-header"
                  onClick={() => setSortByStatus(prev => prev === null ? 'asc' : prev === 'asc' ? 'desc' : null)}
                  title="Click to sort by status"
                >
                  Status {sortByStatus === 'asc' ? '▲' : sortByStatus === 'desc' ? '▼' : ''}
                </th>
                <th>Retries</th>
                <th>Error</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign) => (
                <tr key={campaign.id}>
                  <td className="row-number">{campaign.rowNumber}</td>
                  <td className="platform-id">{campaign.platformCampaignId}</td>
                  <td className="campaign-name">
                    {campaign.campaignId ? (
                      <Link to={`/campaigns/${campaign.campaignId}`} className="campaign-link">
                        {campaign.campaignName || 'View Campaign'}
                      </Link>
                    ) : (
                      campaign.campaignName || '-'
                    )}
                  </td>
                  <td className="daily-budget">{formatCurrency(campaign.dailyBudget)}</td>
                  <td className="status-cell">
                    <StatusBadge status={campaign.status} />
                  </td>
                  <td className="retry-cell">
                    {campaign.retryCount > 0 ? (
                      <span className="retry-info" title={campaign.currentOperation ? `Current operation: ${campaign.currentOperation}` : ''}>
                        {campaign.retryCount}/{campaign.maxRetries}
                        {campaign.currentOperation && ['retrying_import', 'retrying_edit'].includes(campaign.status) && (
                          <span className="retry-operation"> ({campaign.currentOperation})</span>
                        )}
                      </span>
                    ) : (
                      <span className="retry-none">-</span>
                    )}
                  </td>
                  <td className="error-cell" title={campaign.errorDetails?.error || campaign.errorMessage || ''}>
                    {campaign.errorMessage && (
                      campaign.editMessageId ? 'Encountered error during edit' : 'Encountered error during import'
                    )}
                  </td>
                  <td className="actions-cell">
                    {['updated', 'revert_failed'].includes(campaign.status) ? (
                      <button
                        className="revert-button"
                        onClick={() => handleRevertBudget(campaign)}
                        disabled={revertingCampaignId === campaign.id}
                        title="Change budget to a different value"
                      >
                        {revertingCampaignId === campaign.id ? (
                          <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                          <FontAwesomeIcon icon={faRotateLeft} />
                        )}
                        <span className="revert-button-text">{campaign.status === 'revert_failed' ? 'Retry' : 'Revert'}</span>
                      </button>
                    ) : campaign.status === 'reverted' ? (
                      <span className="reverted-label" title={`Reverted at ${new Date(campaign.revertedAt).toLocaleString()}`}>
                        Reverted
                      </span>
                    ) : (
                      <span className="no-action">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* File info */}
      <div className="pacing-file-info">
        <FontAwesomeIcon icon={isDownloadingFile ? faSpinner : faDownload} spin={isDownloadingFile} />
        {pacingData.hasDownloadableFile ? (
          <span
            className="downloadable-file-link"
            onClick={downloadOriginalFile}
            title="Click to download the original file"
          >
            Original file: {pacingData.originalFileName}
          </span>
        ) : (
          <span>Original file: {pacingData.originalFileName}</span>
        )}
      </div>
    </div>
  );
};

export default PacingDetails;

